/**
 * DimensionValueQuickCreateDialog — inline-create wrapper for a value within one
 * analytic dimension, used by DimensionValuePicker's "+ Create" action.
 *
 * Owns the create mutation (scoped to `dimension.id`) and renders the existing
 * DimensionValueFormDialog (the canonical create form — no duplicated fields).
 * Pass `onCreated` so the parent picker stays open and auto-selects the new
 * value; `defaultName` seeds the name field from the picker's search text.
 * Follows the EntityPickerBase + QuickCreateDialog pattern — see
 * docs/design-system/entity-picker-inline-create.md.
 */
import { DimensionValueFormDialog } from './DimensionValueFormDialog';
import { useCreateDimensionValue } from '../hooks/useDimensions';
import type {
  Dimension,
  DimensionValue,
  DimensionValueCreate,
  DimensionValueUpdate,
} from '../types';

interface DimensionValueQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** The axis the new value belongs to (scopes the create + hierarchy fields). */
  dimension: Dimension;
  /** Called with the new value (before the picker auto-selects it). */
  onCreated?: (value: DimensionValue) => void;
  /** Pre-fill the name field (e.g. from the picker's search text). */
  defaultName?: string;
}

export function DimensionValueQuickCreateDialog({
  open,
  onClose,
  dimension,
  onCreated,
  defaultName,
}: DimensionValueQuickCreateDialogProps) {
  const createValue = useCreateDimensionValue();

  const handleSubmit = async (data: DimensionValueCreate | DimensionValueUpdate) => {
    try {
      const created = await createValue.mutateAsync({
        dimensionId: dimension.id,
        data: data as DimensionValueCreate,
      });
      onCreated?.(created.data);
      onClose();
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DimensionValueFormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      dimension={dimension}
      defaultName={defaultName}
      isLoading={createValue.isPending}
    />
  );
}
